import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../contexts/AuthContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  Loader2, Plus, Pencil, Trash2, Factory, BarChart2,
  AlertTriangle, CheckCircle, Clock, List, PlayCircle,
  RefreshCw, Package2, ShoppingCart, TrendingDown, Warehouse,
} from 'lucide-react';

const CUR_YEAR = new Date().getFullYear();
const YEARS = [CUR_YEAR - 1, CUR_YEAR, CUR_YEAR + 1];
const MONTHS = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

const ALERT_CONFIG = {
  red:    { label: 'Critico',      cls: 'text-red-600 dark:text-red-400',    bg: 'bg-red-50    dark:bg-red-950/30    border-red-200    dark:border-red-800'    },
  yellow: { label: 'Scorta bassa', cls: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50  dark:bg-amber-950/30  border-amber-200  dark:border-amber-800'  },
  green:  { label: 'OK',           cls: 'text-green-600 dark:text-green-400', bg: 'bg-green-50  dark:bg-green-950/30   border-green-200  dark:border-green-800'  },
};

const STATUS_CFG = {
  planned:     { label: 'Pianificato',  cls: 'text-blue-600 dark:text-blue-400' },
  in_progress: { label: 'In corso',     cls: 'text-amber-600 dark:text-amber-400' },
  completed:   { label: 'Completato',   cls: 'text-green-600 dark:text-green-400' },
};

const fmt    = (n, d = 2) => n == null ? '—' : Number(n).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: d });
const fmtEur = (n) => n == null ? '—' : `€ ${Number(n).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function AlertDot({ alert }) {
  const cfg = ALERT_CONFIG[alert] || ALERT_CONFIG.green;
  return <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] font-medium ${cfg.bg} ${cfg.cls}`}>{cfg.label}</span>;
}

// ── BOM Form ─────────────────────────────────────────────────────────────────
function BomForm({ open, onClose, onSave, products, materials }) {
  const blank = { product_id: '', material_id: '', quantity_per_unit: '' };
  const [form, setForm] = useState(blank);
  useEffect(() => { if (open) setForm(blank); }, [open]); // eslint-disable-line

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.product_id || !form.material_id || !form.quantity_per_unit) {
      toast.error('Tutti i campi sono obbligatori'); return;
    }
    const prod = products.find(p => p.id === form.product_id);
    const mat  = materials.find(m => m.id === form.material_id);
    onSave({
      product_id:  form.product_id,
      product_name: prod?.description || prod?.product_code || '',
      product_sku:  prod?.product_code || '',
      material_id:  form.material_id,
      material_name: mat?.name || '',
      material_unit: mat?.unit || 'pz',
      quantity_per_unit: parseFloat(form.quantity_per_unit) || 0,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Aggiungi componente distinta base</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 pt-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Articolo *</Label>
            <Select value={form.product_id} onValueChange={v => setForm(f => ({ ...f, product_id: v }))}>
              <SelectTrigger className="h-8"><SelectValue placeholder="Seleziona articolo" /></SelectTrigger>
              <SelectContent className="max-h-48">
                {products.map(p => <SelectItem key={p.id} value={p.id}>{p.description || p.product_code}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Materiale *</Label>
              <Select value={form.material_id} onValueChange={v => setForm(f => ({ ...f, material_id: v }))}>
                <SelectTrigger className="h-8"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent className="max-h-48">
                  {materials.map(m => <SelectItem key={m.id} value={m.id}>{m.name} ({m.unit})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Qtà per unità *</Label>
              <Input type="number" step="0.001" value={form.quantity_per_unit}
                onChange={e => setForm(f => ({ ...f, quantity_per_unit: e.target.value }))} className="h-8" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose} className="h-8">Annulla</Button>
            <Button type="submit" className="h-8">Aggiungi</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Production Plan Form ──────────────────────────────────────────────────────
function PlanForm({ open, onClose, onSave, products, initial, year }) {
  const blank = {
    product_id: '', period_type: 'monthly', period_year: year,
    period_month: new Date().getMonth() + 1, planned_qty: '', forecast_qty: '', notes: '',
  };
  const [form, setForm] = useState(blank);
  useEffect(() => {
    setForm(initial ? { ...blank, ...initial } : { ...blank, period_year: year });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial, year]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.product_id || !form.planned_qty) { toast.error('Articolo e quantità sono obbligatori'); return; }
    const prod = products.find(p => p.id === form.product_id);
    onSave({
      ...form,
      product_name: prod?.description || prod?.product_code || '',
      period_year: parseInt(form.period_year),
      period_month: parseInt(form.period_month) || null,
      planned_qty: parseFloat(form.planned_qty) || 0,
      forecast_qty: parseFloat(form.forecast_qty) || 0,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
            {initial ? 'Modifica Piano' : 'Nuovo Piano Produzione'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 pt-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Articolo *</Label>
            <Select value={form.product_id} onValueChange={v => setForm(f => ({ ...f, product_id: v }))}>
              <SelectTrigger className="h-8"><SelectValue placeholder="Seleziona articolo" /></SelectTrigger>
              <SelectContent className="max-h-48">
                {products.map(p => <SelectItem key={p.id} value={p.id}>{p.description || p.product_code}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Mese</Label>
              <Select value={String(form.period_month)} onValueChange={v => setForm(f => ({ ...f, period_month: v }))}>
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Qtà pianificata *</Label>
              <Input type="number" value={form.planned_qty} onChange={e => setForm(f => ({ ...f, planned_qty: e.target.value }))} className="h-8" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Qtà forecast vendite (riferimento)</Label>
            <Input type="number" value={form.forecast_qty} onChange={e => setForm(f => ({ ...f, forecast_qty: e.target.value }))} className="h-8" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Note</Label>
            <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="h-8" />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose} className="h-8">Annulla</Button>
            <Button type="submit" className="h-8">{initial ? 'Salva' : 'Crea piano'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Register Production Dialog ────────────────────────────────────────────────
function RegisterDialog({ open, onClose, plan, onDone }) {
  const [qty, setQty] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (open) { setQty(''); setNotes(''); } }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!qty) { toast.error('Inserisci la quantità prodotta'); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/production/plans/register`, {
        plan_id: plan.id, actual_qty: parseFloat(qty), notes, production_date: date,
      });
      toast.success(r.data.message);
      onDone();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore registrazione'); }
    finally { setLoading(false); }
  };

  if (!plan) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Registra produzione</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 pt-2">
          <div className="rounded-lg bg-muted/50 p-3 text-xs space-y-1">
            <p className="font-medium">{plan.product_name}</p>
            <p className="text-muted-foreground">
              Pianificato: {fmt(plan.planned_qty)} · Già prodotto: {fmt(plan.actual_qty)} pz
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Quantità prodotta *</Label>
              <Input type="number" value={qty} onChange={e => setQty(e.target.value)} className="h-8" autoFocus />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Data produzione</Label>
              <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="h-8" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Note</Label>
            <Input value={notes} onChange={e => setNotes(e.target.value)} className="h-8" />
          </div>
          <p className="text-xs text-amber-600 dark:text-amber-400">
            La registrazione scaricherà automaticamente i materiali dalla distinta base.
          </p>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose} className="h-8">Annulla</Button>
            <Button type="submit" disabled={loading} className="h-8 gap-2">
              {loading ? <Loader2 size={12} className="animate-spin" /> : <PlayCircle size={12} />}
              Registra
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function ProductionPage() {
  const { user } = useAuth();
  const [year, setYear]   = useState(CUR_YEAR);
  const [month, setMonth] = useState(new Date().getMonth() + 1);

  // Data
  const [products, setProducts]   = useState([]);
  const [materials, setMaterials] = useState([]);
  const [bom, setBom]             = useState([]);
  const [plans, setPlans]         = useState([]);
  const [requirements, setRequirements] = useState(null);
  const [consuntivo, setConsuntivo]     = useState(null);

  const [loading, setLoading]   = useState(false);
  const [bomForm, setBomForm]   = useState(false);
  const [planForm, setPlanForm] = useState({ open: false, initial: null });
  const [regDialog, setRegDialog] = useState({ open: false, plan: null });
  const [seeding, setSeeding]   = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');

  const isAdmin = ['admin', 'super_admin'].includes(user?.role);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, mRes, bRes, plRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/inventory/materials`),
        axios.get(`${API}/production/bom`),
        axios.get(`${API}/production/plans`, { params: { year, month } }),
      ]);
      setProducts(pRes.data.map(p => ({ ...p, id: p.id || p._id })));
      setMaterials(mRes.data);
      setBom(bRes.data);
      setPlans(plRes.data);
    } catch { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  }, [year, month]);

  const loadRequirements = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/production/requirements`, { params: { year, month } });
      setRequirements(r.data);
    } catch { toast.error('Errore fabbisogni'); }
  }, [year, month]);

  const loadConsuntivo = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/production/consuntivo`, { params: { year, month } });
      setConsuntivo(r.data);
    } catch { toast.error('Errore consuntivo'); }
  }, [year, month]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => {
    if (activeTab === 'forecast')   loadRequirements();
    if (activeTab === 'consuntivo') loadConsuntivo();
  }, [activeTab, loadRequirements, loadConsuntivo]);

  const saveBom = async (data) => {
    try {
      await axios.post(`${API}/production/bom`, data);
      toast.success('Componente aggiunto');
      setBomForm(false);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const deleteBom = async (id) => {
    try {
      await axios.delete(`${API}/production/bom/${id}`);
      toast.success('Rimosso');
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const savePlan = async (data) => {
    try {
      if (planForm.initial) {
        await axios.put(`${API}/production/plans/${planForm.initial.id}`, data);
        toast.success('Piano aggiornato');
      } else {
        await axios.post(`${API}/production/plans`, data);
        toast.success('Piano creato');
      }
      setPlanForm({ open: false, initial: null });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const deletePlan = async (id) => {
    try {
      await axios.delete(`${API}/production/plans/${id}`);
      toast.success('Piano eliminato');
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const seedDemoBom = async () => {
    setSeeding(true);
    try {
      const r = await axios.post(`${API}/production/seed-demo-bom`);
      toast.success(r.data.message);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore seed demo'); }
    finally { setSeeding(false); }
  };

  // Group BOM by product
  const bomByProduct = {};
  bom.forEach(b => {
    if (!bomByProduct[b.product_id]) bomByProduct[b.product_id] = { product_name: b.product_name, items: [] };
    bomByProduct[b.product_id].items.push(b);
  });

  const planTotal = plans.reduce((s, p) => ({ planned: s.planned + (p.planned_qty || 0), actual: s.actual + (p.actual_qty || 0) }), { planned: 0, actual: 0 });
  const reqList = requirements?.requirements || [];
  const criticalReq = reqList.filter(r => r.alert !== 'green').length;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Produzione</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Distinta base, piani di produzione e fabbisogni materiali</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Year + Month */}
          <div className="flex items-center gap-1">
            {YEARS.map(y => (
              <button key={y} onClick={() => setYear(y)}
                className={`px-2.5 py-1 rounded-lg text-xs font-bold border transition-all ${y === year ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-muted'}`}>
                {y}
              </button>
            ))}
          </div>
          <Select value={String(month)} onValueChange={v => setMonth(parseInt(v))}>
            <SelectTrigger className="h-7 w-20 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={loadAll} className="gap-1.5"><RefreshCw size={12} /></Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard"><BarChart2 size={12} className="mr-1.5" />Dashboard</TabsTrigger>
          <TabsTrigger value="bom" data-testid="tab-bom"><List size={12} className="mr-1.5" />Distinta Base</TabsTrigger>
          <TabsTrigger value="forecast" data-testid="tab-forecast-prod"><ShoppingCart size={12} className="mr-1.5" />Pianificazione</TabsTrigger>
          <TabsTrigger value="consuntivo" data-testid="tab-consuntivo"><TrendingDown size={12} className="mr-1.5" />Consuntivo</TabsTrigger>
        </TabsList>

        {/* ── Dashboard ── */}
        <TabsContent value="dashboard" className="mt-4 space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Articoli a catalogo',   value: products.length,      icon: Package2,     color: 'text-blue-600' },
              { label: 'Piani produzione',       value: plans.length,         icon: Factory,      color: 'text-purple-600' },
              { label: 'Tot. pianificato',       value: `${fmt(planTotal.planned, 0)} pz`, icon: ShoppingCart, color: 'text-amber-600', isText: true },
              { label: 'Tot. prodotto',          value: `${fmt(planTotal.actual, 0)} pz`,  icon: CheckCircle,  color: 'text-green-600', isText: true },
            ].map(k => (
              <Card key={k.label}>
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center">
                      <k.icon size={16} className={k.color} />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">{k.label}</p>
                      <p className={`text-xl font-bold ${k.color}`}>{k.value}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {plans.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Piani — {MONTHS[month - 1]} {year}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/40 text-muted-foreground">
                      <th className="text-left px-4 py-2">Articolo</th>
                      <th className="text-right px-4 py-2">Pianificato</th>
                      <th className="text-right px-4 py-2">Prodotto</th>
                      <th className="text-right px-4 py-2">Avanzamento</th>
                      <th className="text-left px-4 py-2">Stato</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plans.map(p => {
                      const pct = p.planned_qty > 0 ? Math.min(100, (p.actual_qty / p.planned_qty) * 100) : 0;
                      const sc = STATUS_CFG[p.status] || STATUS_CFG.planned;
                      return (
                        <tr key={p.id} className="border-b hover:bg-muted/20">
                          <td className="px-4 py-2 font-medium">{p.product_name}</td>
                          <td className="px-4 py-2 text-right">{fmt(p.planned_qty, 0)}</td>
                          <td className="px-4 py-2 text-right font-semibold">{fmt(p.actual_qty, 0)}</td>
                          <td className="px-4 py-2 text-right">
                            <div className="flex items-center gap-2 justify-end">
                              <Progress value={pct} className="h-1.5 w-16" />
                              <span className="text-muted-foreground">{pct.toFixed(0)}%</span>
                            </div>
                          </td>
                          <td className={`px-4 py-2 font-medium ${sc.cls}`}>{sc.label}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {plans.length === 0 && bom.length === 0 && (
            <Card>
              <CardContent className="py-8 text-center">
                <Factory size={32} className="mx-auto mb-3 text-muted-foreground/50" />
                <p className="text-sm font-medium mb-2">Nessun dato di produzione</p>
                <p className="text-xs text-muted-foreground mb-4">
                  Inizia caricando la distinta base demo per i tuoi articoli.
                </p>
                <Button onClick={seedDemoBom} disabled={seeding} size="sm" variant="outline" className="gap-2">
                  {seeding ? <Loader2 size={12} className="animate-spin" /> : <Factory size={12} />}
                  Carica distinta base demo
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ── Distinta Base ── */}
        <TabsContent value="bom" className="mt-4">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-muted-foreground">{bom.length} componenti in {Object.keys(bomByProduct).length} articoli</p>
            <div className="flex gap-2">
              <Button onClick={seedDemoBom} disabled={seeding} size="sm" variant="outline" className="gap-1.5">
                {seeding ? <Loader2 size={12} className="animate-spin" /> : <Factory size={12} />}
                Demo BOM
              </Button>
              {isAdmin && (
                <Button onClick={() => setBomForm(true)} size="sm" className="gap-1.5" data-testid="new-bom-btn">
                  <Plus size={12} />Aggiungi componente
                </Button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-10"><Loader2 size={18} className="animate-spin text-muted-foreground" /></div>
          ) : Object.keys(bomByProduct).length === 0 ? (
            <Card><CardContent className="py-10 text-center text-muted-foreground text-sm">
              Nessuna distinta base definita. Aggiungila o carica i dati demo.
            </CardContent></Card>
          ) : (
            <div className="space-y-4">
              {Object.entries(bomByProduct).map(([pid, { product_name, items }]) => (
                <Card key={pid}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      <Package2 size={13} className="text-primary" />
                      {product_name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-muted/40 text-muted-foreground">
                          <th className="text-left px-4 py-2">Materiale</th>
                          <th className="text-right px-4 py-2">Qtà / unità prodotto</th>
                          <th className="text-left px-4 py-2">UdM</th>
                          {isAdmin && <th className="w-10"></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {items.map(item => (
                          <tr key={item.id} className="border-b hover:bg-muted/20 group">
                            <td className="px-4 py-2 font-medium">{item.material_name}</td>
                            <td className="px-4 py-2 text-right font-semibold">{fmt(item.quantity_per_unit, 4)}</td>
                            <td className="px-4 py-2 text-muted-foreground">{item.material_unit}</td>
                            {isAdmin && (
                              <td className="px-4 py-2">
                                <button onClick={() => deleteBom(item.id)}
                                  className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-950 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <Trash2 size={11} className="text-red-500" />
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ── Pianificazione (Forecast + MRP) ── */}
        <TabsContent value="forecast" className="mt-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm font-semibold">{MONTHS[month - 1]} {year} — Piano produzione e fabbisogni materiali</p>
              {criticalReq > 0 && (
                <p className="text-xs text-red-500 dark:text-red-400 flex items-center gap-1 mt-0.5">
                  <AlertTriangle size={10} />{criticalReq} materiali critici o in esaurimento
                </p>
              )}
            </div>
            {isAdmin && (
              <Button onClick={() => setPlanForm({ open: true, initial: null })} size="sm" className="gap-1.5" data-testid="new-plan-btn">
                <Plus size={12} />Nuovo piano
              </Button>
            )}
          </div>

          {/* Production Plans */}
          <Card className="mb-5">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Piani di Produzione
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/40 text-muted-foreground">
                    <th className="text-left px-4 py-2">Articolo</th>
                    <th className="text-right px-4 py-2">Forecast vendite</th>
                    <th className="text-right px-4 py-2">Da produrre</th>
                    <th className="text-right px-4 py-2">Già prodotto</th>
                    <th className="text-left px-4 py-2">Stato</th>
                    {isAdmin && <th className="w-24"></th>}
                  </tr>
                </thead>
                <tbody>
                  {plans.length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">
                      Nessun piano per questo periodo. Crea il primo piano di produzione.
                    </td></tr>
                  ) : plans.map(p => {
                    const sc = STATUS_CFG[p.status] || STATUS_CFG.planned;
                    return (
                      <tr key={p.id} className="border-b hover:bg-muted/20 group">
                        <td className="px-4 py-2 font-medium">{p.product_name}</td>
                        <td className="px-4 py-2 text-right text-muted-foreground">{p.forecast_qty > 0 ? fmt(p.forecast_qty, 0) : '—'}</td>
                        <td className="px-4 py-2 text-right font-semibold">{fmt(p.planned_qty, 0)}</td>
                        <td className="px-4 py-2 text-right text-green-600 dark:text-green-400">{fmt(p.actual_qty, 0)}</td>
                        <td className={`px-4 py-2 font-medium ${sc.cls}`}>{sc.label}</td>
                        {isAdmin && (
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                              <button onClick={() => setRegDialog({ open: true, plan: p })}
                                className="p-1 rounded hover:bg-green-50 dark:hover:bg-green-950" title="Registra produzione">
                                <PlayCircle size={11} className="text-green-600" />
                              </button>
                              <button onClick={() => setPlanForm({ open: true, initial: p })}
                                className="p-1 rounded hover:bg-muted">
                                <Pencil size={11} className="text-muted-foreground" />
                              </button>
                              <button onClick={() => deletePlan(p.id)}
                                className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-950">
                                <Trash2 size={11} className="text-red-500" />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Material Requirements */}
          {reqList.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <ShoppingCart size={13} className="text-primary" />
                  Fabbisogni materiali (MRP) — Acquisti suggeriti
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/40 text-muted-foreground">
                      <th className="text-center px-3 py-2 w-8"></th>
                      <th className="text-left px-3 py-2">Materiale</th>
                      <th className="text-left px-3 py-2">SKU</th>
                      <th className="text-right px-3 py-2">Fabbisogno</th>
                      <th className="text-right px-3 py-2">Disponibile</th>
                      <th className="text-right px-3 py-2">Acquisto suggerito</th>
                      <th className="text-right px-3 py-2">Costo stimato</th>
                      <th className="text-left px-3 py-2">Fornitore</th>
                      <th className="text-right px-3 py-2">Lead time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reqList.map(r => (
                      <tr key={r.material_id} className="border-b hover:bg-muted/20">
                        <td className="px-3 py-2 text-center"><AlertDot alert={r.alert} /></td>
                        <td className="px-3 py-2 font-medium">{r.material_name}</td>
                        <td className="px-3 py-2 font-mono text-muted-foreground">{r.sku || '—'}</td>
                        <td className="px-3 py-2 text-right">{fmt(r.total_need, 4)} {r.material_unit}</td>
                        <td className={`px-3 py-2 text-right font-medium ${r.alert === 'red' ? 'text-red-500' : r.alert === 'yellow' ? 'text-amber-600' : ''}`}>
                          {fmt(r.available_qty, 4)} {r.material_unit}
                        </td>
                        <td className={`px-3 py-2 text-right font-bold ${r.suggested_purchase_qty > 0 ? 'text-red-500 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                          {r.suggested_purchase_qty > 0 ? `${fmt(r.suggested_purchase_qty, 4)} ${r.material_unit}` : '✓ sufficiente'}
                        </td>
                        <td className="px-3 py-2 text-right">{r.suggested_cost > 0 ? fmtEur(r.suggested_cost) : '—'}</td>
                        <td className="px-3 py-2 text-muted-foreground">{r.supplier || '—'}</td>
                        <td className="px-3 py-2 text-right text-muted-foreground">{r.lead_time_days}gg</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ── Consuntivo ── */}
        <TabsContent value="consuntivo" className="mt-4 space-y-5">
          {!consuntivo ? (
            <div className="flex justify-center py-10"><Loader2 size={18} className="animate-spin text-muted-foreground" /></div>
          ) : (
            <>
              {/* Production summary */}
              {consuntivo.production_summary?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Produzione — Pianificato vs Consuntivo
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-muted/40 text-muted-foreground">
                          <th className="text-left px-4 py-2">Articolo</th>
                          <th className="text-right px-4 py-2">Forecast vendite</th>
                          <th className="text-right px-4 py-2">Venduto reale</th>
                          <th className="text-right px-4 py-2">Pianificato prod.</th>
                          <th className="text-right px-4 py-2">Prodotto</th>
                          <th className="text-right px-4 py-2">Var. %</th>
                          <th className="text-right px-4 py-2">Completam.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {consuntivo.production_summary.map(p => (
                          <tr key={p.product_id} className="border-b hover:bg-muted/20">
                            <td className="px-4 py-2 font-medium">{p.product_name}</td>
                            <td className="px-4 py-2 text-right text-muted-foreground">{p.forecast_qty > 0 ? fmt(p.forecast_qty, 0) : '—'}</td>
                            <td className="px-4 py-2 text-right">{fmt(p.actual_sales, 0)}</td>
                            <td className="px-4 py-2 text-right">{fmt(p.planned_qty, 0)}</td>
                            <td className="px-4 py-2 text-right font-semibold text-green-600 dark:text-green-400">{fmt(p.actual_qty, 0)}</td>
                            <td className={`px-4 py-2 text-right font-semibold ${(p.variance_pct || 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
                              {p.variance_pct != null ? `${p.variance_pct >= 0 ? '+' : ''}${p.variance_pct}%` : '—'}
                            </td>
                            <td className="px-4 py-2 text-right">{p.completion_pct != null ? `${p.completion_pct}%` : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              )}

              {/* Material coverage */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <Warehouse size={13} className="text-primary" />
                    Copertura Scorte — Semaforo
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/40 text-muted-foreground">
                        <th className="text-center px-3 py-2 w-8"></th>
                        <th className="text-left px-3 py-2">Materiale</th>
                        <th className="text-right px-3 py-2">Giacenza</th>
                        <th className="text-right px-3 py-2">Scorta min</th>
                        <th className="text-right px-3 py-2">Cons. 30gg</th>
                        <th className="text-right px-3 py-2">Cons./giorno</th>
                        <th className="text-right px-3 py-2">Copertura (gg)</th>
                        <th className="text-left px-3 py-2">Lead time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(consuntivo.material_coverage || []).map(m => (
                        <tr key={m.id} className="border-b hover:bg-muted/20">
                          <td className="px-3 py-2 text-center"><AlertDot alert={m.alert} /></td>
                          <td className="px-3 py-2 font-medium">{m.name}</td>
                          <td className={`px-3 py-2 text-right font-semibold ${m.alert === 'red' ? 'text-red-500 dark:text-red-400' : m.alert === 'yellow' ? 'text-amber-600 dark:text-amber-400' : ''}`}>
                            {fmt(m.current_stock, 4)} {m.unit}
                          </td>
                          <td className="px-3 py-2 text-right text-muted-foreground">{fmt(m.min_stock, 4)}</td>
                          <td className="px-3 py-2 text-right text-muted-foreground">{m.consumption_30d > 0 ? fmt(m.consumption_30d, 4) : '—'}</td>
                          <td className="px-3 py-2 text-right text-muted-foreground">{m.avg_daily_consumption != null ? fmt(m.avg_daily_consumption, 3) : '—'}</td>
                          <td className={`px-3 py-2 text-right font-bold ${
                            m.coverage_days == null ? 'text-muted-foreground' :
                            m.coverage_days < (m.lead_time_days || 7) ? 'text-red-500 dark:text-red-400' :
                            m.coverage_days < (m.lead_time_days || 7) * 2 ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'
                          }`}>
                            {m.coverage_days != null ? `${m.coverage_days} gg` : '∞'}
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">{m.lead_time_days}gg</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <BomForm open={bomForm} onClose={() => setBomForm(false)} onSave={saveBom} products={products} materials={materials} />
      <PlanForm open={planForm.open} onClose={() => setPlanForm({ open: false, initial: null })} onSave={savePlan}
        products={products} initial={planForm.initial} year={year} />
      <RegisterDialog open={regDialog.open} onClose={() => setRegDialog({ open: false, plan: null })}
        plan={regDialog.plan} onDone={loadAll} />
    </div>
  );
}
